import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const C = {
  parchment: '#FFFEFC', ink: '#0D0D0D', wheat: '#C8A84B',
  muted: '#8A8070', border: '#E8E0D0', white: '#FFFFFF',
};

const SITE_URL = 'https://baking-journal.vercel.app';

function Field({ label, type, value, onChange, placeholder, onKeyDown }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, margin: '0 0 6px', fontFamily: 'Poppins, sans-serif' }}>
        {label}
      </p>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        style={{
          width: '100%', boxSizing: 'border-box', fontSize: 13,
          padding: '10px 12px', border: `0.5px solid ${C.border}`,
          borderRadius: 8, fontFamily: 'Poppins, sans-serif',
          color: C.ink, background: C.white, outline: 'none',
        }}
      />
    </div>
  );
}

function PrimaryBtn({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', padding: '12px 0', border: 'none',
        borderRadius: 8, background: C.wheat, color: '#fff',
        fontSize: 11, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'Poppins, sans-serif', letterSpacing: '0.06em',
        textTransform: 'uppercase', opacity: disabled ? 0.4 : 1,
        marginBottom: 20,
      }}
    >
      {children}
    </button>
  );
}

function LinkText({ children, onClick }) {
  return (
    <p
      onClick={onClick}
      style={{
        fontSize: 12, color: C.muted, textAlign: 'center',
        cursor: 'pointer', fontFamily: 'Poppins, sans-serif', margin: '0 0 10px',
      }}
    >
      {children}
    </p>
  );
}

export default function Auth({ initialView, onResetDone }) {
  const [view, setView] = useState(initialView || 'signin'); // signin | signup | forgot | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');


  function reset() { setError(''); setMessage(''); }
  function go(v) { reset(); setEmail(''); setPassword(''); setConfirmPassword(''); setView(v); }

  async function handleSignIn() {
    setLoading(true); reset();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  }

  async function handleSignUp() {
    setLoading(true); reset();
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) setError(error.message);
    else setMessage('Check your email to confirm your account.');
    setLoading(false);
  }

  async function handleForgotPassword() {
    setLoading(true); reset();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: SITE_URL,
    });
    if (error) setError(error.message);
    else setMessage('Password reset link sent — check your email.');
    setLoading(false);
  }

  async function handleResetPassword() {
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    setLoading(true); reset();
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setError(error.message); }
    else { setMessage('Password updated. Signing you in…'); if (onResetDone) onResetDone(); }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh', background: C.parchment, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
      fontFamily: 'Poppins, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <p style={{ fontSize: 26, fontWeight: 200, color: C.ink, margin: '0 0 40px', letterSpacing: '-0.02em' }}>
          homer
        </p>

        {/* Sign In */}
        {view === 'signin' && (
          <>
            <Field label="email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
            <div style={{ marginBottom: 8 }}>
              <Field label="password" type="password" value={password} onChange={setPassword} placeholder="your password" onKeyDown={e => e.key === 'Enter' && handleSignIn()} />
            </div>
            <p onClick={() => go('forgot')} style={{ fontSize: 11, color: C.muted, textAlign: 'right', cursor: 'pointer', margin: '0 0 20px', fontFamily: 'Poppins, sans-serif' }}>
              forgot password?
            </p>
            {error && <p style={{ fontSize: 12, color: '#c0392b', margin: '0 0 14px' }}>{error}</p>}
            {message && <p style={{ fontSize: 12, color: C.wheat, margin: '0 0 14px' }}>{message}</p>}
            <PrimaryBtn onClick={handleSignIn} disabled={loading || !email || !password}>
              {loading ? 'please wait…' : 'sign in'}
            </PrimaryBtn>
            <LinkText onClick={() => go('signup')}>don't have an account? sign up</LinkText>
          </>
        )}

        {/* Sign Up */}
        {view === 'signup' && (
          <>
            <Field label="email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
            <Field label="password" type="password" value={password} onChange={setPassword} placeholder="minimum 6 characters" onKeyDown={e => e.key === 'Enter' && handleSignUp()} />
            {error && <p style={{ fontSize: 12, color: '#c0392b', margin: '0 0 14px' }}>{error}</p>}
            {message && <p style={{ fontSize: 12, color: C.wheat, margin: '0 0 14px' }}>{message}</p>}
            <PrimaryBtn onClick={handleSignUp} disabled={loading || !email || !password}>
              {loading ? 'please wait…' : 'create account'}
            </PrimaryBtn>
            <LinkText onClick={() => go('signin')}>already have an account? sign in</LinkText>
          </>
        )}

        {/* Forgot Password */}
        {view === 'forgot' && (
          <>
            <p style={{ fontSize: 13, color: C.muted, margin: '0 0 24px', lineHeight: 1.6 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <Field label="email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" onKeyDown={e => e.key === 'Enter' && handleForgotPassword()} />
            {error && <p style={{ fontSize: 12, color: '#c0392b', margin: '0 0 14px' }}>{error}</p>}
            {message && <p style={{ fontSize: 12, color: C.wheat, margin: '0 0 14px' }}>{message}</p>}
            <PrimaryBtn onClick={handleForgotPassword} disabled={loading || !email}>
              {loading ? 'sending…' : 'send reset link'}
            </PrimaryBtn>
            <LinkText onClick={() => go('signin')}>← back to sign in</LinkText>
          </>
        )}

        {/* Reset Password */}
        {view === 'reset' && (
          <>
            <p style={{ fontSize: 13, color: C.muted, margin: '0 0 24px', lineHeight: 1.6 }}>
              Choose a new password for your account.
            </p>
            <Field label="new password" type="password" value={password} onChange={setPassword} placeholder="minimum 6 characters" />
            <Field label="confirm password" type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="repeat your password" onKeyDown={e => e.key === 'Enter' && handleResetPassword()} />
            {error && <p style={{ fontSize: 12, color: '#c0392b', margin: '0 0 14px' }}>{error}</p>}
            {message && <p style={{ fontSize: 12, color: C.wheat, margin: '0 0 14px' }}>{message}</p>}
            <PrimaryBtn onClick={handleResetPassword} disabled={loading || !password || !confirmPassword}>
              {loading ? 'updating…' : 'update password'}
            </PrimaryBtn>
          </>
        )}
      </div>
    </div>
  );
}
